import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Users, ImagePlus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Kpi, Block } from "@/components/crm/RoleDashboard";
import { Announcements } from "@/components/crm/hub/Announcements";
import { ProfileDialog } from "@/components/crm/ProfileDialog";
import { ChangePasswordDialog } from "@/components/crm/ChangePasswordDialog";
import { Avatar3D, useUserImage } from "@/components/crm/UserImage";

const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };

function PortalCover({ user, sub }) {
  const cover = useUserImage(user, "cover");
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState(false);
  return (
    <div data-testid="portal-cover" className="relative fade-up">
      <div className="cover-3d h-36 rounded-2xl bg-cover bg-center sm:h-44" style={cover ? { backgroundImage: `url(${cover})` } : undefined} />
      <button data-testid="portal-edit-profile" onClick={() => setOpen(true)} className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1.5 text-xs font-semibold text-[#102A25] backdrop-blur hover:bg-white"><ImagePlus className="h-3.5 w-3.5" />Edit profile</button>
      <div className="-mt-12 flex items-end gap-4 px-5">
        <button data-testid="portal-avatar" onClick={() => setOpen(true)} className="rounded-full ring-4 ring-white/80"><Avatar3D user={user} size={96} /></button>
        <div className="pb-2">{sub}</div>
      </div>
      <ProfileDialog open={open} onOpenChange={setOpen} onPassword={() => setPw(true)} />
      <ChangePasswordDialog open={pw} onOpenChange={setPw} />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/my").then((r) => setD(r.data)).catch((e) => setD({ error: e.response?.data?.detail || "Portal unavailable" })); }, []);
  return (
    <>
      <PortalCover user={user} sub={<span data-testid="portal-name" className="text-sm font-bold text-zinc-700">{user.name}</span>} />
      <div className="flex flex-col gap-3 fade-up md:flex-row md:items-center md:justify-between">
        <div>
          <h1 data-testid="page-title" className="text-2xl font-bold tracking-tight text-zinc-900 sm:text-[28px]">{greeting()}, {user.name}</h1>
          <p data-testid="my-portal-subtitle" className="mt-1 text-sm text-zinc-500">
            {d?.me?.code && <span className="font-mono font-bold text-[#8B7356]">{d.me.code} · </span>}{[d?.me?.title, d?.me?.department].filter(Boolean).join(" · ") || "My portal"} — only your own work and data.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {d?.team_overview && <Link to="/team-overview" data-testid="team-overview-link" className="flex h-10 items-center gap-2 rounded-lg border border-[#D6C6B0] bg-white px-3.5 text-sm font-semibold text-[#8B7356] hover:bg-[#F3ECE2]"><Users className="h-4 w-4" />Team overview</Link>}
          <span data-testid="dashboard-date" className="flex h-10 items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3.5 text-sm text-zinc-600"><CalendarDays className="h-4 w-4 text-zinc-400" />{new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
        </div>
      </div>
      <Announcements />
      {d?.error && <div data-testid="my-portal-error" className="rounded-xl border border-rose-400/40 p-4 text-sm text-rose-500">{String(d.error)}</div>}
      {d?.kpis && (
        <div data-testid="my-portal" className="space-y-5">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">{d.kpis.map((k) => <Kpi key={k.label} k={k} />)}</div>
          <div className="grid gap-5 lg:grid-cols-2">{d.blocks.map((b) => <Block key={b.key} b={b} />)}</div>
        </div>
      )}
    </>
  );
}
